import React from 'react';
import { NotificationRule, notificationTypes, NotificationValue, notifSeverities } from '@sp-types/notificationRule';
import {
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Account } from '../../../sp-types/accountTypes';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { CloseButton } from '../../../components/UI-Elements/Buttons/CloseButton';
import { OverlayDisplayDiv } from '../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { SeverityIcon } from '../../NotificationRules/comoponents/NotificationsTable';

interface Props {
  rule: NotificationRule;
  close: (_rule?: NotificationRule) => void;
  accounts: Account[];
  isNew?: boolean;
}

export const EditNotificationRule: React.FC<Props> = ({ rule, close, accounts, isNew = false }) => {
  const { materials } = useMaterialsStore();
  const [editedRule, setEditedRule] = React.useState<NotificationRule>(rule);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (rule) {
      setEditedRule(rule);
    }
  }, [rule]);

  const setRule = (fieldName: string, value: string | boolean | number | any) => {
    setEditedRule((prev) => ({ ...prev, [fieldName]: value }));
    setIsDirty(true);
  };

  const isDisabled = (): boolean => {
    return !(editedRule.type && editedRule.condition) || !isDirty;
  };
  return (
    <OverlayDisplayDiv setDisplayOverlay={() => close()} hideClose>
      <div className="w-fit h-200 relative bg-white rounded shadow p-3 flex flex-col gap-5">
        <div className="absolute top-3 right-3">
          <CloseButton onClick={close} />
        </div>
        <h4> {isNew ? 'Create new' : 'Edit'} Notification rule</h4>
        <div className="text-gray-300">id: {isNew ? '' : editedRule.id}</div>
        <div className="flex gap-3 w-fit">
          <div className="flex flex-col gap-3 w-72">
            <TextField
              className=""
              onChange={({ target }) => setRule('name', target.value)}
              value={editedRule.name}
              label="Rule name"
            />

            <FormControl required>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                className=""
                value={editedRule.type}
                title={editedRule.type}
                onChange={({ target }) => setRule('type', target.value as NotificationValue)}
              >
                {notificationTypes
                  ?.filter((a) => a.value)
                  .map(({ value, label }, i) => (
                    <MenuItem key={i} value={value}>
                      {label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {editedRule.type === 'price_target' && (
              <TextField
                type="number"
                required
                onChange={({ target }) => setRule('options', { ...editedRule.options, compareDays: target.value })}
                value={editedRule.options?.compareDays}
                label="Compare to X days ago"
              />
            )}
            {['backwardation', 'contango'].includes(editedRule.type) && (
              <FormControl>
                <InputLabel>Compare month</InputLabel>
                <Select
                  label="Compare mon"
                  value={editedRule.options?.compareMonth}
                  onChange={({ target }) => setRule('options', { ...editedRule.options, compareMonth: target.value })}
                >
                  {Array.from({ length: 12 }).map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      Future month {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <div className="flex gap-2 flex-1">
              <TextField
                type="number"
                required
                onChange={({ target }) => setRule('condition', target.value)}
                value={editedRule.condition}
                label="Condition ($)"
              />
              {['price_target', 'backwardation', 'contango'].includes(editedRule.type) && (
                <FormControl className="flex flex-1">
                  <InputLabel>Condition type</InputLabel>
                  <Select
                    label="Condition type"
                    value={editedRule.options?.conditionType}
                    onChange={({ target }) =>
                      setRule('options', { ...editedRule.options, conditionType: target.value })
                    }
                  >
                    <MenuItem value="%">%</MenuItem>
                    <MenuItem value="$">$</MenuItem>
                  </Select>
                </FormControl>
              )}
            </div>
            <TextField
              type="number"
              onChange={({ target }) => setRule('expireHours', +target.value)}
              value={editedRule.expireHours}
              label="Notification expire in hours"
            />
          </div>
          <div className="flex flex-col gap-3 w-72">
            <FormControl>
              <InputLabel>Severity:</InputLabel>
              <Select
                className=""
                label="Severity"
                value={editedRule.severity}
                onChange={({ target }) => setRule('severity', target.value)}
              >
                {notifSeverities.map((severity, i) => (
                  <MenuItem key={i} value={severity}>
                    <SeverityIcon severity={severity} /> &nbsp; {severity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Account:</InputLabel>
              <Select
                className=""
                label="Account"
                value={editedRule.accountId}
                onChange={({ target }) => setRule('accountId', target.value)}
              >
                {accounts?.map((account, i) => (
                  <MenuItem key={i} value={account.id}>
                    {account.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Material</InputLabel>
              <Select
                className=""
                title={editedRule.materialId}
                label="Material"
                value={editedRule.materialId}
                onChange={({ target }) => setRule('materialId', target.value || '')}
              >
                {materials.map((mat) => (
                  <MenuItem key={mat.id} value={mat.id}>
                    {mat.name} ({mat.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              onChange={({ target }) => setRule('sendEveryDays', +target.value)}
              value={editedRule.sendEveryDays}
              label="Notify every X days?"
            />
            <FormControlLabel
              control={
                <Checkbox checked={editedRule.enabled} onChange={({ target }) => setRule('enabled', target.checked)} />
              }
              label="Enabled"
            />
          </div>
        </div>
        <div className="text-gray-300">Created at: {editedRule.created_at?.toDate().toLocaleDateString('en-CA')}</div>
        <Button variant="contained" onClick={() => close(editedRule)} disabled={isDisabled()}>
          Save
        </Button>
      </div>
    </OverlayDisplayDiv>
  );
};
